import { weekDelta } from './compare';
import { fm, fr, weekdayShort } from './dates';
import { AMBER, f0, f1, MUTED } from './format';
import { FIBRE_ERROR, FIBRE_LOADING } from './fibre';
import { weekName } from './weeks';

// The KPI block (KpiStrip's primary, secondary and lists) for each Daily Log
// tab, over the selected week, with notes comparing it to the compared week.

/** Check-in days below this make the weekly symptom average provisional. */
export const MIN_CHECK_INS = 4;

const kpi = (label, value, unit, scope, note = '', noteFg = MUTED) => ({
  label,
  value,
  unit,
  scope,
  note,
  noteFg,
  noteW: /^[↑↓]/.test(note) ? 600 : 400,
});

/** "↑ 12 vs last week" → "↑ 12 pp vs last week" for percentage-point changes. */
const pp = { dir: 1, fmt: f0, unit: ' pp', whole: true };

function symptomsKpis(ctx, cmp) {
  const { cur, ref } = cmp;
  const S = cur.sym;
  const nDays = cur.days.length;
  const [note, fg] = weekDelta(cmp, S.avg, ref.sym.avg, { dir: -1, fmt: f1 });
  const provisional = S.avg != null && S.nCheck < MIN_CHECK_INS;
  const top = (by, fmt) => ctx.names
    .filter((n) => by[n] != null && by[n] > 0)
    .sort((a, b) => by[b] - by[a])
    .slice(0, 3)
    .map((n) => ({ name: n, txt: fmt(by[n]) }));
  return {
    primary: kpi('Average symptom severity', f1(S.avg), '/5', `${weekName(ctx, cur.start)} · ${fr(cur.start, cur.end)} · check-in days only`,
      provisional ? `Provisional · ${S.nCheck} check-in day${S.nCheck === 1 ? '' : 's'} (min ${MIN_CHECK_INS})` : note,
      provisional ? AMBER : fg),
    secondary: [
      kpi('Check-ins', `${S.nCheck} of ${nDays}`, '', 'Days with a gut symptom entry', 'Days with no check-in are left out'),
      kpi('Symptom-free days', String(S.free), '', 'Confirmed by client', 'Never inferred from missing entries'),
      kpi('Highest daily average', S.hi ? f1(S.hi.avg) : '–', '/5', S.hi ? `${weekdayShort(S.hi.iso)}, ${fm(S.hi.iso)}` : 'No check-ins',
        'Daily average = 4 scored symptoms ÷ 4'),
    ],
    lists: S.nCheck
      ? [
        { title: 'Highest average severity', scope: weekName(ctx, cur.start), items: top(S.sub, (v) => `${f1(v)}/5`) },
        { title: 'Most frequent', scope: `Days rated above 0, of ${S.nCheck} check-in${S.nCheck === 1 ? '' : 's'}`, items: top(S.freq, (v) => `${v} of ${S.nCheck} days`) },
      ]
      : [],
  };
}

function bowelKpis(ctx, cmp) {
  const { cur, ref } = cmp;
  const B = cur.bowel;
  const [n1, c1] = weekDelta(cmp, B.normal, ref.bowel.normal, pp);
  const [n2, c2] = weekDelta(cmp, B.comfort, ref.bowel.comfort, { dir: 1, fmt: f0, whole: true });
  const daysWithData = cur.days.filter((d) => ctx.bowel[d]).length;
  return {
    primary: kpi('Stool consistency', f0(B.normal), B.normal == null ? '' : '%',
      `Movements in Bristol Type 3–4 · ${B.good} of ${B.typed} this week`, n1, c1),
    secondary: [
      kpi('Bowel comfort', f0(B.comfort), B.comfort == null ? '' : '%', 'Urgency, straining & complete evacuation', n2, c2),
      kpi('Movements per day', daysWithData ? f1(B.n / daysWithData) : '–', '', `${daysWithData} day${daysWithData === 1 ? '' : 's'} with bowel data`, 'Not scored'),
      kpi('Incomplete evacuation', `${B.inc} of ${B.known}`, '', 'Movements with evacuation recorded',
        B.n > B.known ? `${B.n - B.known} not recorded` : ''),
    ],
    lists: B.n
      ? [{
        title: 'Bristol Stool Scale',
        scope: `${weekName(ctx, cur.start)} · ${B.typed} movement${B.typed === 1 ? '' : 's'}`,
        items: [['Hard · 1–2', B.hard], ['Normal · 3–4', B.good], ['Loose · 5–7', B.loose]].map(([name, v]) => ({ name, txt: String(v) })),
      }]
      : [],
  };
}

/** A KPI for a source still loading or failed: "…" / "–" with why. */
const pending = (label, status, [loading, error]) =>
  kpi(label, status === 'loading' ? '…' : '–', '', status === 'loading' ? loading : error);

const FIBRE_MSG = [FIBRE_LOADING, FIBRE_ERROR];

function fibreKpis(ctx, cmp) {
  const { cur, ref } = cmp;
  const F = cur.fibre;
  const T = ctx.fibreTarget;
  if (F.status !== 'ready') {
    return { adherence: pending('Fibre adherence', F.status, FIBRE_MSG), average: pending('Average fibre', F.status, FIBRE_MSG) };
  }
  // The compared week may still be loading, or have failed: no delta until it's in.
  const [n1, c1] = ref.fibre.status === 'ready' ? weekDelta(cmp, F.pct, ref.fibre.pct, pp) : ['', MUTED];
  return {
    adherence: T
      ? kpi('Fibre adherence', f0(F.pct), F.pct == null ? '' : '%', `${F.hit} of ${F.n} logged days at ${f0(T)} g or more`, n1, c1)
      : kpi('Fibre adherence', '–', '', 'No fibre target set'),
    average: kpi('Average fibre', f0(F.avg), T ? `g / ${f0(T)} g` : 'g', 'Logged days only', ''),
  };
}

/** Nutrition: fibre adherence, average fibre, and the days with diet tracking. */
function nutritionKpis(ctx, cmp) {
  const F = fibreKpis(ctx, cmp);
  const { cur } = cmp;
  const days = cur.fibre.status === 'ready'
    ? kpi('Days logged', `${cur.fibre.n} of ${cur.days.length}`, '', 'Days with diet tracking', 'Untracked days are “Not logged”, never 0 g')
    : pending('Days logged', cur.fibre.status, FIBRE_MSG);
  return { primary: F.adherence, secondary: [F.average, days], lists: [] };
}

/** @param {ReturnType<import('./compare').compareWith>} cmp */
export function buildKpis(ctx, tab, cmp) {
  if (tab === 'bowel') return bowelKpis(ctx, cmp);
  if (tab === 'nut') return nutritionKpis(ctx, cmp);
  return symptomsKpis(ctx, cmp);
}
